import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import { mainSiteUrl } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";
import { SITE_URL, SITE_NAME, buildArticleJsonLd, languageCode } from "@/lib/seo";

export default function ArticlePage() {
  const { t } = useTranslation();
  const { lang, slug } = useParams<{ lang: string; slug: string }>();
  const navigate = useNavigate();
  const currentLang = lang || "pt";
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  const c = article
    ? currentLang === "pt"
      ? article.pt
      : currentLang === "de"
        ? article.de
        : article.en
    : undefined;

  const articleCanonical = `${SITE_URL}/${currentLang}/article/${slug || ""}/`;
  const articleImage = article?.image;

  useSeo(
    c
      ? {
          title: c.title,
          description: c.excerpt,
          keywords: "cssbuy spreadsheet, cssbuy, cssbuy guide, cssbuy products, cssbuy sellers",
          canonical: articleCanonical,
          ogTitle: c.title,
          ogDescription: c.excerpt,
          ogType: "article",
          ogUrl: articleCanonical,
          ogImage: articleImage,
          ogSiteName: SITE_NAME,
          twitterCard: "summary_large_image",
          twitterTitle: c.title,
          twitterDescription: c.excerpt,
          twitterImage: articleImage,
        }
      : {
          title: `${t("notFound.title")} | CSSBuyBR`,
          canonical: articleCanonical,
        },
    c
      ? buildArticleJsonLd({
          headline: c.title,
          description: c.excerpt,
          image: articleImage,
          canonical: articleCanonical,
          inLanguage: languageCode(currentLang),
          faq: c.faq.map((f) => ({ question: f.q, answer: f.a })),
        })
      : undefined
  );

  if (!article || !c) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header transparent={false} />
        <div className="pt-32 pb-20 px-4 text-center">
          <h1 className="font-heading text-2xl text-foreground-950 mb-4">
            {t("notFound.title")}
          </h1>
          <button
            onClick={() => navigate(`/${currentLang}/`)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
          >
            {t("notFound.back")}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header transparent={false} />

      <div className="pt-20 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
            <button
              onClick={() => navigate(`/${currentLang}/`)}
              className="hover:text-primary-600 transition-colors"
            >
              {t("article.breadcrumbHome")}
            </button>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 truncate">{article.category}</span>
          </nav>

          <article className="article-content">
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4 leading-tight">
              {c.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {t("article.author")}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {t("article.published")}: 2026-08-30
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {t("article.updated")}: 2026-08-30
              </span>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={c.title}
                className="w-full h-56 md:h-72 object-cover"
              />
            </div>

            <div className="bg-accent-50/80 border-l-4 border-accent-500 rounded-r-lg p-5 mb-8">
              <p className="text-foreground-700 text-sm leading-relaxed m-0 font-medium">
                {c.intro}
              </p>
            </div>

            {c.sections.map((section, i) => (
              <div key={i}>
                <h2>{section.heading}</h2>
                <p>{section.body}</p>
              </div>
            ))}

            <div className="my-10">
              <h2>FAQ</h2>
              <div className="space-y-3">
                {c.faq.map((faq, i) => (
                  <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background-100/50 transition-colors"
                    >
                      <span className="font-medium text-foreground-900 text-sm pr-4">{faq.q}</span>
                      <i className={`ri-arrow-down-s-line text-lg text-foreground-400 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`}></i>
                    </button>
                    {openFaq === i && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-primary-50 border border-primary-200/50 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-primary-800 mb-3">
                {currentLang === "pt" ? "Pronto para explorar?" : currentLang === "de" ? "Bereit zu erkunden?" : "Ready to explore?"}
              </h3>
              <p className="text-sm text-primary-700 mb-4">
                {currentLang === "pt"
                  ? "Acesse o CSSBuy Spreadsheet e comece sua pesquisa de produtos e vendedores."
                  : currentLang === "de"
                    ? "Greifen Sie auf CSSBuy Spreadsheet zu und beginnen Sie Ihre Produkt- und Verkäuferrecherche."
                    : "Access CSSBuy Spreadsheet and start your product and seller research."}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
                >
                  {t("cta.mainSite")}
                  <i className="ri-external-link-line"></i>
                </a>
                <button
                  onClick={() => navigate(`/${currentLang}/`)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-50 border border-primary-300 text-primary-700 rounded-lg font-medium hover:bg-primary-50 transition-all whitespace-nowrap"
                >
                  <i className="ri-home-4-line"></i>
                  {t("nav.home")}
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
}